import { MockVerdictBank } from './mock-matcher';

export interface MockCandidate {
  name: string;
  /** The wire carries the current role and employer separately, so the mock does too. */
  title: string;
  company: string;
  location: string;
  fileName: string;
  /** How long before the seed this candidate was matched, so the list spans several days. */
  matchedMinutesAgo: number;
  verdicts: MockVerdictBank;
}

/**
 * MOCK ONLY. Arjun Mehta's verdicts are the sample match result from Recruit Service, verbatim
 * (the name is invented). The other four candidates are invented so the ranked list has range.
 */
export const MOCK_CANDIDATES: MockCandidate[] = [
  {
    "name": "Arjun Mehta",
    "title": "Senior Salesforce developer",
    "company": "IDFC FIRST Bank",
    "location": "Bengaluru",
    "fileName": "Arjun_Mehta_Resume.pdf",
    "matchedMinutesAgo": 4320,
    "verdicts": {
      "r0": [
        "match",
        "Dynamic Senior Salesforce Developer with 9+ years of experience driving innovation and enhancing user experiences across\nvarious domains, including healthcare and banking.",
        "The resume directly states 9+ years of experience, which meets the requirement of at least 5 years of relevant professional experience."
      ],
      "r2": [
        "match",
        "Senior Salesforce Developer\nIDFC FIRST Bank, Bengaluru January 2022 – Present",
        "The resume directly shows the candidate working in Bengaluru, which satisfies being based in Bangalore for a work-from-office requirement."
      ],
      "r3": [
        "match",
        "Developed a feedback forum using Visualforce and Service Cloud for Gilead Sciences (pharmaceutical), gaining experience in healthcare domain workflows and compliance-sensitive environments.",
        "This quote shows hands-on frontend/UI work through building a feedback forum, and the resume also shows backend work elsewhere through Apex, integrations, and solution delivery, so the requirement for both frontend and backend experience is met."
      ],
      "r4": [
        "partial",
        "Developed a feedback forum using Visualforce and Service Cloud for Gilead Sciences (pharmaceutical), gaining experience in healthcare domain workflows and compliance-sensitive environments.",
        "This shows some direct frontend/UI work through building a feedback forum and the resume also lists UI-related technologies elsewhere, but no single passage demonstrates strong enough end-to-end frontend depth for a full-stack role."
      ],
      "r5": [
        "not_available",
        "",
        "The resume shows backend-related Salesforce work and lists JavaScript, but it does not mention substantial backend experience in Java, Python, NodeJS, or Golang anywhere in the document."
      ],
      "r6": [
        "not_available",
        "",
        "The resume shows Salesforce, Apex, JavaScript, integrations, and related platform work, but nowhere mentions NodeJS or a strong proxy that would demonstrate backend experience with NodeJS."
      ],
      "r7": [
        "not_available",
        "",
        "No mention of AWS or a strong proxy for AWS exposure appears anywhere in the resume."
      ],
      "r8": [
        "partial",
        "Dynamic Senior Salesforce Developer with 9+ years of experience driving innovation and enhancing user experiences across\nvarious domains, including healthcare and banking. Expert in leveraging Lightning, Apex, and SOQL to implement robust,\nscalable solutions.",
        "The resume directly states expertise with SOQL, which is Salesforce's SQL-like query language, showing relevant SQL exposure but not explicit general SQL depth."
      ],
      "r9": [
        "partial",
        "Expert in leveraging Lightning, Apex, and SOQL to implement robust,\nscalable solutions.",
        "Lightning component work implies handling UI state, but the resume never says how state is managed."
      ]
    }
  },
  {
    "name": "Priya Raman",
    "title": "Senior software engineer",
    "company": "Swiggy",
    "location": "Bengaluru",
    "fileName": "Priya_Raman_CV.pdf",
    "matchedMinutesAgo": 2880,
    "verdicts": {
      "r0": [
        "match",
        "Full-stack engineer with 7 years of experience building consumer-scale ordering and payments systems.",
        "States 7 years of professional experience, above the 5-year minimum."
      ],
      "r2": [
        "match",
        "Senior Software Engineer, Swiggy, Bengaluru · March 2021 – Present",
        "Currently working in Bengaluru, which satisfies the office location requirement."
      ],
      "r3": [
        "match",
        "Built the restaurant partner dashboard in React and the Node.js order-routing service behind it.",
        "Shows hands-on work on both the React frontend and the Node.js backend of the same product."
      ],
      "r4": [
        "match",
        "Led the migration of the partner dashboard from AngularJS to React, with a shared component library used by 6 teams.",
        "Owning a frontend migration and a shared component library shows UI depth beyond occasional exposure."
      ],
      "r5": [
        "match",
        "5 years of NodeJS (Express, NestJS) and 2 years of Java (Spring Boot) in production services.",
        "Explicitly states multi-year backend work in NodeJS and Java."
      ],
      "r6": [
        "match",
        "5 years of NodeJS (Express, NestJS) and 2 years of Java (Spring Boot) in production services.",
        "Five years of production NodeJS experience is stated directly."
      ],
      "r7": [
        "partial",
        "Deployed services on AWS ECS with CloudWatch alerting.",
        "Mentions AWS deployment and monitoring, but not broader AWS architecture or ownership."
      ],
      "r8": [
        "match",
        "Designed PostgreSQL schemas and query optimisations that cut order lookup latency by 40%.",
        "Schema design and query optimisation in PostgreSQL shows good SQL exposure."
      ],
      "r9": [
        "not_available",
        "",
        "The resume is backend-focused and says nothing about frontend or UI state."
      ]
    }
  },
  {
    "name": "Karthik Iyer",
    "title": "Software engineer II",
    "company": "Razorpay",
    "location": "Bengaluru",
    "fileName": "Karthik_Iyer.pdf",
    "matchedMinutesAgo": 1560,
    "verdicts": {
      "r0": [
        "match",
        "Backend-leaning full-stack engineer, 6 years across fintech and e-commerce.",
        "States 6 years of experience, above the 5-year minimum."
      ],
      "r2": [
        "match",
        "Razorpay, Bengaluru · 2022 – Present",
        "Currently based in Bengaluru."
      ],
      "r3": [
        "match",
        "Shipped the merchant settlements UI in React and the Golang reconciliation service it calls.",
        "Shows hands-on work on both a React frontend and a Golang backend."
      ],
      "r4": [
        "partial",
        "Contributed React screens for the settlements dashboard.",
        "Shows React contributions to one dashboard, but not ownership of frontend architecture."
      ],
      "r5": [
        "match",
        "4 years of Golang microservices handling settlement reconciliation at 2M transactions a day.",
        "States four years of production Golang backend work at meaningful scale."
      ],
      "r6": [
        "not_available",
        "",
        "The resume lists Golang and Python for backend work and does not mention NodeJS."
      ],
      "r7": [
        "partial",
        "Familiar with AWS S3 and SQS for batch pipelines.",
        "Mentions specific AWS services used in pipelines, which is limited but relevant exposure."
      ],
      "r8": [
        "partial",
        "Wrote reporting queries in MySQL.",
        "Mentions MySQL reporting queries without evidence of schema design or tuning."
      ],
      "r9": [
        "match",
        "Built the merchant dashboard in React, moving shared state onto Redux Toolkit to kill prop drilling.",
        "Names the state library and the problem it solved, which answers how state is managed in the UI."
      ]
    }
  },
  {
    "name": "Rohan Das",
    "title": "Technical lead",
    "company": "Infosys",
    "location": "Hyderabad",
    "fileName": "Rohan_Das_Resume.docx",
    "matchedMinutesAgo": 240,
    "verdicts": {
      "r0": [
        "match",
        "12 years in enterprise Java development, the last 4 as a technical lead.",
        "States 12 years of experience, well above the minimum."
      ],
      "r2": [
        "not_available",
        "",
        "The resume lists Hyderabad as the current location and does not mention Bangalore or willingness to relocate."
      ],
      "r3": [
        "match",
        "Built Angular admin portals and the Spring Boot APIs behind them for a retail banking client.",
        "Shows hands-on work on both an Angular frontend and Spring Boot backend."
      ],
      "r4": [
        "partial",
        "Built Angular admin portals and the Spring Boot APIs behind them for a retail banking client.",
        "One project shows Angular work, but most of the resume describes backend and team leadership."
      ],
      "r5": [
        "match",
        "Java and Spring Boot across 12 years of backend services for banking clients.",
        "Long, direct Java backend experience is stated."
      ],
      "r6": [
        "not_available",
        "",
        "No mention of NodeJS."
      ],
      "r7": [
        "match",
        "Migrated three on-premise services to AWS using EC2, RDS and Lambda.",
        "Leading an AWS migration across several services shows good AWS exposure."
      ],
      "r8": [
        "match",
        "Oracle and PostgreSQL: stored procedures, indexing and query tuning.",
        "Stored procedures, indexing and tuning show good SQL exposure."
      ],
      "r9": [
        "partial",
        "Led a team delivering Angular front ends for banking clients.",
        "Front-end delivery is stated, but nothing describes how state was managed."
      ]
    }
  },
  {
    "name": "Neha Kulkarni",
    "title": "Senior frontend engineer",
    "company": "Druva",
    "location": "Pune",
    "fileName": "Neha_Kulkarni_CV.pdf",
    "matchedMinutesAgo": 20,
    "verdicts": {
      "r0": [
        "match",
        "6 years building web applications, primarily in React and TypeScript.",
        "States 6 years of experience, above the 5-year minimum."
      ],
      "r2": [
        "partial",
        "Based in Pune. Open to relocating to Bangalore.",
        "States openness to relocation, but is not currently based in Bangalore."
      ],
      "r3": [
        "partial",
        "Built BFF endpoints in Node.js for the backup console.",
        "Shows some backend work through BFF endpoints, but the resume is mainly frontend."
      ],
      "r4": [
        "match",
        "Owned the backup console frontend: React, Redux Toolkit, design system and performance budgets.",
        "Ownership of a production frontend end to end shows strong UI depth."
      ],
      "r5": [
        "partial",
        "Two years writing Node.js BFF services.",
        "NodeJS backend work is stated, but at the BFF layer rather than substantial backend depth."
      ],
      "r6": [
        "match",
        "Two years writing Node.js BFF services.",
        "Directly states Node.js backend work."
      ],
      "r7": [
        "not_available",
        "",
        "No mention of AWS."
      ],
      "r8": [
        "not_available",
        "",
        "No mention of SQL or relational databases."
      ],
      "r9": [
        "match",
        "Owned Druva's console front end: React with Zustand for client state and React Query for server cache.",
        "Separates client state from server cache and names both tools, a direct answer to how UI state is managed."
      ]
    }
  }
];

export const SAMPLE_RESUME_FILES = MOCK_CANDIDATES.map((c) => c.fileName);

/** A resume the parser cannot read. Nothing is ever announced for it — see the mock's upload. */
export const SAMPLE_UNREADABLE_RESUME_FILE = 'Unreadable scan.pdf';
